import { cadenceBlockingStateValues } from '@poseidon/types/types/cadenceState.type';

import { cn } from './cn';
import { Button, Tooltip } from './Input2';
import { Editor, General, Security } from './StrokeIcons';

type ContactCardNameRowPureProps = {
  name: string;
  honorificTitle?: string | null;
  liOpenLink?: boolean | null;
  /** Whether the contact is a 1st-degree LinkedIn connection. When true, the InMail
      affordance icon (open-profile check / lock) is hidden — InMail does not apply to
      contacts you're already connected with. */
  liConnected?: boolean | null;
  cadenceName?: string;
  /** When true, the cadence-name chip is NOT rendered on the name line — the parent
   *  renders it on its own row instead. Used by pipeline cards so the long campaign
   *  name ("Personalized LinkedIn Prospecting") stops squeezing the contact name. */
  hideCadenceName?: boolean;
  cadenceIsActive?: boolean;
  /** Current cadence state. Used to suppress the generic NOT-ACTIVE badge when the
      cadence is in a terminal state (finished, responded, rejected, etc.), since those
      already render their own, more specific badge — showing both is redundant. */
  cadenceState?: string;
  hasResponse?: boolean;
  size?: 'default' | 'pipeline';
  disqualified?: boolean;
  /** Plain-text preview of the contact's notes (HTML stripped). When present, renders a small
      yellow pencil after the name; tooltip shows the preview. Mirrors the yellow-pencil notes
      indicator on the contact detail tab nav so the visual is recognizable. */
  notesPreview?: string | null;
};

export default function ContactCardNameRowPure({
  name,
  honorificTitle,
  liOpenLink,
  liConnected,
  cadenceName,
  hideCadenceName,
  cadenceIsActive,
  cadenceState,
  hasResponse,
  size = 'default',
  disqualified,
  notesPreview,
}: ContactCardNameRowPureProps) {
  // InMail only matters for non-connections. For 1st-degree connections there is no
  // InMail restriction, so neither the "open profile" check nor the lock should show.
  const showInMailAffordance = liConnected !== true;
  // A terminal cadence (finished, responded, rejected, …) already shows its own specific
  // badge, so the generic NOT-ACTIVE pill is redundant noise there. Only surface NOT-ACTIVE
  // for inactive-but-non-terminal cadences (paused, not-started, …). Terminal == any state
  // not in the blocking set; an unknown/missing state stays non-terminal (badge shows).
  const stateIsTerminal =
    cadenceState != null && !(cadenceBlockingStateValues as readonly string[]).includes(cadenceState);
  const showNotActive = cadenceIsActive === false && !stateIsTerminal;
  return (
    <div
      className={cn(
        'flex min-h-5 items-center gap-1.5 text-sm font-medium',
        size === 'pipeline' && 'min-h-3 text-xs gap-1',
      )}>
      <span className="min-w-0 grow truncate">
        {honorificTitle && <span className="text-gray-400 dark:text-gray-500">{honorificTitle} </span>}
        {name}
      </span>
      {showNotActive && (
        <Button size="2xs" color="yellow" tone="soft" readOnly tooltip="This campaign is not active">
          NOT-ACTIVE
        </Button>
      )}
      {showInMailAffordance && liOpenLink === true && (
        <Tooltip tooltip="This LinkedIn feature allows you to send an inmail to this person for free">
          <General.CheckCircle
            data-testid="inmail-open-icon"
            className="size-3.5 text-slate-500 dark:text-slate-400 shrink-0"
          />
        </Tooltip>
      )}
      {showInMailAffordance && liOpenLink === false && (
        <Tooltip tooltip="This LinkedIn feature does not allow you to send an inmail to this person for free">
          <Security.Lock01
            data-testid="inmail-lock-icon"
            className="size-3.5 text-slate-500 dark:text-slate-400 shrink-0"
          />
        </Tooltip>
      )}
      {notesPreview != null && notesPreview.length > 0 && (
        <Tooltip tooltip={notesPreview} tooltipMaxWidth={320}>
          <Editor.Pencil01 className="size-3.5 shrink-0 text-yellow-500 dark:text-yellow-400" />
        </Tooltip>
      )}
      {cadenceName && !hideCadenceName && (
        <Button size="2xs" readOnly>
          {cadenceName}
        </Button>
      )}
      {hasResponse && (
        <Button size="2xs" color="red" tone="soft" readOnly tooltip="Lead responded" className="mt-0.5">
          REPLY
        </Button>
      )}
      {disqualified && (
        <Button size="2xs" color="yellow" tone="soft" readOnly tooltip="Contact disqualified" className="mt-0.5">
          DISQUALIFIED
        </Button>
      )}
    </div>
  );
}
